<template>
  <div class="flex flex-grow">
    <div v-if="state.hasAdminPrivileges" class="fixed z-10 w-full text-white bg-black">
      <div class="container flex justify-between px-6 py-3">
        <div class="self-center">Admin Console</div>
        <div class="flex gap-4">
          <ClientOnly>
            <VButton
              v-if="userState.flags.ACCESS_VIDEO_RECORDING_MANAGEMENT_ENABLED"
              class="hidden md_block"
              size="sm"
              :component="NuxtLink"
              :to="`${vpeUrl}/video-recordings/${uuid}?redirect=${locationHref}`"
            >
              <PencilIcon class="inline-block w-4 mr-2" />
              Edit
            </VButton>
          </ClientOnly>
          <VButton
            v-if="state.videoOptions?.src"
            component="a"
            size="sm"
            :href="state.videoOptions?.src"
            download
            data-testid="download-recording-btn"
            @click="trackDownload"
          >
            <ArrowDownTrayIcon class="inline-block w-4 mr-2" />
            Download
          </VButton>
        </div>
      </div>
    </div>
    <div class="container">
      <ClientOnly>
        <ModalsShare
          v-if="state.company"
          :share-url="shareLink"
          :title="state.company.name"
          :showing="showSocialMediaShareModal"
          analytics-prefix="VideoRecordingLandingPage"
          :analytics-payload="{ companyId: state.company.id, companyName: state.company.name }"
          data-testid="share-modal"
          @update:showing="showSocialMediaShareModal = $event"
        >
          <button v-if="state.hasAdminPrivileges" class="flex flex-col items-center justify-center" @click="handleEmbedClick">
            <CodeBracketIcon class="w-10 h-12 text-gray-900" />
            <p class="mb-2 font-normal text-gray-700">Embed</p>
          </button>
        </ModalsShare>
      </ClientOnly>
      <main :class="[state.hasAdminPrivileges ? 'py-10' : 'pb-10', 'h-full']">
        <LoadingSpinner v-if="state.loading" class="mix-blend-difference" size="sm" :transparent-container="true" />
        <section class="flex flex-col h-full">
          <ClientOnly>
            <div class="relative flex-grow my-10 w-full sm_min-h-80 aspect-video">
              <VideoPlayer :options="state.videoOptions" />
            </div>
          </ClientOnly>
          <div class="flex-row items-center justify-between md_flex flex-nowrap">
            <div class="flex-1 mb-6 overflow-hidden md_mb-2 mr-11">
              <h1 class="m-0 text-2xl font-normal truncate md_text-4xl" style="line-height: inherit" data-testid="recording-name">
                {{ state.recordingName || state.company?.name }}
              </h1>
              <ClientOnly>
                <p class="mb-4 text-base font-medium text-gray-700 leading-6">
                  {{ formatDate(state.recordingDate) }}
                </p>
              </ClientOnly>
              <VLink
                v-if="state.company"
                class="text-sm text-copper-700"
                component="button"
                font-weight="normal"
                @click="trackCompanyLinkClick"
              >
                Learn more about {{ state.company.name }}
              </VLink>
            </div>
            <div class="self-start">
              <VButton size="sm" data-testid="social-media-share-btn" class="w-full" @click="showSocialMediaShareModal = true">
                <ShareIcon class="inline-block w-4 mr-2" />
                Share
              </VButton>
            </div>
          </div>
          <div v-if="descriptionTruncated?.length" class="pt-4 mt-4 text-sm border-t border-gray-400">
            <div class="cursor-pointer whitespace-pre-line" @click="toggleTruncated">
              {{ showTruncated ? descriptionTruncated : state.recordingDescription }}
            </div>
            <div v-if="state.recordingDescription && state.recordingDescription.length > 256" class="mt-4 text-right">
              <VButton size="sm" @click="toggleTruncated">{{ showTruncated ? 'Read more' : 'Read less' }}</VButton>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import type { Company } from '@/entities/company'
import type { PlayerOptions } from '@/entities/videoPlayer'
import type { Meeting } from '@/entities/meeting'
import { DateTime } from 'luxon'

interface State {
  loading: boolean
  company: Company | null
  company_id: number | null
  hasAdminPrivileges: boolean
  videoOptions: Partial<PlayerOptions> | undefined
  recordingName: string | null
  recordingDate: string | null
  deckId: number | null
  recordingDescription: string
  analyticsPayload?: {
    companyName: string
    companyId: number
  }
}
</script>

<script lang="ts" setup>
import { ShareIcon, ArrowDownTrayIcon, PencilIcon, CodeBracketIcon } from '@heroicons/vue/24/solid'
import type { Deck } from '@/entities/deck'
import type { Recording } from '@/entities/recordings'

definePageMeta({ layout: 'recordings' })
const NuxtLink = resolveComponent('NuxtLink')

const config = useRuntimeConfig()
const { $analytics, $sentry } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { state: userState } = useUser()

const locationHref = computed(() => {
  if (process.client) {
    return window.location.href
  }

  return ''
})

const shareLink = computed(() => {
  if (process.client && location) return location.origin + location.pathname

  return ''
})

const uuid = computed(() => (Array.isArray(route.params.id) ? route.params.id[0] : route.params.id))

const state = reactive<State>({
  loading: true,
  hasAdminPrivileges: false,
  company: null,
  company_id: null,
  videoOptions: undefined,
  analyticsPayload: undefined,
  recordingName: null,
  recordingDate: '',
  deckId: null,
  recordingDescription: '',
})

const recording = ref<Recording | null>(null)

const descriptionTruncated = computed(() => {
  if (state.recordingDescription && state.recordingDescription.length > 256) {
    return state.recordingDescription.substring(0, 256) + '...'
  } else {
    return state.recordingDescription
  }
})

try {
  const res = await fetchRecording(uuid.value)

  recording.value = res

  state.recordingName = res.name
  state.recordingDate = res.started_at
  state.videoOptions = { src: res.download_url, type: 'video/mp4', poster: res.thumbnail }

  if (res.conference) {
    state.deckId = res.conference.deck_id
  }

  if (import.meta.client) {
    state.company = await fetchCompany(res.company_id)

    state.analyticsPayload = {
      companyName: state.company?.name ?? '',
      companyId: state.company_id ?? 0,
    }

    const defaultDescription = `This video includes ${state.company?.name} providing an update using VRIFY interactive content.`
    state.recordingDescription = res.description || defaultDescription

    $analytics.track('VideoRecordingLandingPageLoad', state.analyticsPayload)
  }
} catch (e) {
  if (e instanceof Error) {
    $sentry?.captureException(e)
  }

  router.push('/notfound')
} finally {
  state.loading = false
}

onMounted(async () => {
  if (recording.value?.conference) {
    state.hasAdminPrivileges = recording.value.show_admin_panel
  }
})

const pageTitle = computed(() => {
  if (state.recordingName) {
    return state.recordingName
  }

  if (state.company?.name) {
    return state.company.name
  }

  return undefined
})

// Needs to be after setup above for data
useHead({
  title: pageTitle.value,
  meta: [
    {
      name: 'description',
      content: state.recordingDescription,
    },
    {
      property: 'og:description',
      content: state.recordingDescription,
    },
    {
      property: 'og:type',
      content: 'video',
    },
    {
      property: 'og:image',
      content: state.company?.logo ?? '',
    },
    {
      property: 'og:video:url',
      content: state.videoOptions?.src,
    },
  ],
})

const showSocialMediaShareModal = ref(false)

function fetchRecording(uuid: string) {
  return $fetch<Recording>(`v2/meetings/recordings/${uuid}`, {
    ...useFetchSharedOptions(),
    credentials: 'include',
    params: { include: ['conference', 'thumbnail'] },
  })
}

async function fetchCompany(companyId: number): Promise<Company | null> {
  return await $fetch<Company>(`v2/companies/${companyId}`, useFetchSharedOptions())
}

function fetchDeck(deckId: number): Promise<Deck | null> {
  return $fetch<Deck>(`v2/decks/${deckId}`, {
    ...useFetchSharedOptions(),
    credentials: 'include',
  })
}

async function trackDownload() {
  $analytics.track('VideoRecordingLandingPageClickDownload', state.analyticsPayload)
}

async function trackCompanyLinkClick() {
  $analytics.track('VideoRecordingLandingPageClickLearnAboutCompany', state.analyticsPayload)
  await navigateTo(`${config.public.WEB_ENDPOINT}companies/${state.company?.url_slug || state.company?.id}`, { external: true })
}

const formatDate = (date: string | null) => date && DateTime.fromISO(date).toFormat('ff')

async function handleEmbedClick() {
  const trackingPayload: any = {
    ...state.analyticsPayload,
    source: 'Video Landing Page',
  }
  if (state.deckId) {
    trackingPayload.deckId = state.deckId
    const deck = await fetchDeck(state.deckId)
    trackingPayload.deckName = deck?.title
  }
  $analytics.track('VideoRecordingLandingPageClickEmbed', trackingPayload)
  _goToEmbedPage()
}

const vpeUrl = config.public.ADMIN_ENDPOINT
async function _goToEmbedPage() {
  $analytics.track(`VideoRecordingLandingPageClickEmbedShare`, { companyId: state.company?.id, companyName: state.company?.name })
  const existingEmbeds = await useApiFetch<{ results: any[]; count: number }>(
    `v2/companies/${state.company?.id}/embed/videos?meeting_record__uuid=${uuid.value}`,
  )
  switch (existingEmbeds.data.value?.count) {
    case 0:
      // create new embed
      await navigateTo(`/admin/embeds/create`, { external: true })
      break
    case 1:
      // go to existing embed
      const [existingEmbed] = existingEmbeds.data.value.results
      await navigateTo(`/admin/embeds/video/${existingEmbed.id}`, { external: true })
      break
    default:
      // show all existing embeds
      await navigateTo(`/admin/embeds?searchTerm=${state.recordingName}`, { external: true })
  }
}

const showTruncated = ref(true)
function toggleTruncated() {
  showTruncated.value = !showTruncated.value
}
</script>
